import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Heart, Users, Camera, Sparkles, ExternalLink } from 'lucide-react';
import Button from './Button';
import './GLOPage.css';

const GLOPage: React.FC = () => {
  const [activeImage, setActiveImage] = useState<string | null>(null);

  const makeupImages: string[] = [
    "/images/glo/gallery/AZ6A0017-Enhanced-NR.jpg",
    "/images/glo/gallery/AZ6A9863-Enhanced-NR.jpg",
    "/images/glo/gallery/AZ6A9871-Enhanced-NR.jpg",
    "/images/glo/gallery/AZ6A9928-Enhanced-NR.jpg",
    "/images/glo/gallery/AZ6A9937-Enhanced-NR.jpg",
    "/images/glo/gallery/AZ6A9969-Enhanced-NR.jpg",
    "/images/glo/gallery/DSC_0583.jpg",
    "/images/glo/gallery/DSC_0595.jpg",
    "/images/glo/gallery/DSC_0596.jpg",
    "/images/glo/gallery/DSC_4079.jpg",
    "/images/glo/gallery/IMG_9861.jpg",
    "/images/glo/gallery/IMG_9863.jpg",
    "/images/glo/gallery/IMG_9875.jpg",
    "/images/glo/gallery/IMG_9883.jpg",
  ];
  // Shuffle images once per mount
  const shuffledMakeupImages = useMemo(() => {
    const arr = [...makeupImages];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }, []);

  const row1 = shuffledMakeupImages.slice(0, 7);
  const row2 = shuffledMakeupImages.slice(7, 14);

  return (
    <div className="glo-page">
      {/* Behind-the-Scenes Gallery (Makeup) */}
      {/* New section placed near the top to highlight the experience */}
      
      <Helmet>
        <title>GLO – Girls Lifting Others | Carly Faye Foundation</title>
        <meta name="description" content="GLO is a program of the Carly Faye Foundation offering transformative makeover days for girls facing pediatric cancer." />
        <link rel="canonical" href="https://carly-faye-foundation.web.app/glo" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="GLO – Girls Lifting Others" />
        <meta property="og:description" content="A program of the Carly Faye Foundation supporting girls facing pediatric cancer." />
        <meta property="og:url" content="https://carly-faye-foundation.web.app/glo" />
        <meta property="og:image" content="/images/glo/glo-logo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="GLO – Girls Lifting Others" />
        <meta name="twitter:description" content="A program of the Carly Faye Foundation supporting girls facing pediatric cancer." />
        <meta name="twitter:image" content="/images/glo/glo-logo.png" />
      </Helmet>
      {/* Hero Section */}
      <section className="glo-hero">
        <div className="glo-hero-background">
          <img src="/images/carly-faye-foundation-hero.jpg" alt="" className="glo-hero-image" />
          <div className="glo-hero-overlay"></div>
        </div>
        <div className="container">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="glo-hero-content"
          >
            <div className="hero-icon">
              <img src="/images/glo/glo-logo.png" alt="GLO Logo" className="glo-logo" />
            </div>
            <h1>Girls Lifting Others</h1>
            <p>
            A day of beauty, confidence, and hope for girls facing pediatric cancer.
            </p>
            <div className="hero-actions">
              <Button 
                href="https://girlsliftingothers.com/" 
                variant="primary" 
                size="large"
                target="_blank"
                rel="noopener noreferrer"
              >
                Visit GLO Website
                <ExternalLink size={16} />
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* About GLO Section */}
      <section className="glo-about">
        <div className="container">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="glo-about-content"
          >
            <div className="about-grid">
              <div className="about-column">
                <h2>Every girl deserves to feel beautiful, strong, and full of hope.</h2>
                <p>
                For girls ages 10–21 facing the challenges of pediatric cancer, GLO is more than just a makeover — it's a day to shine. We open the doors to a world of pampering, with professional hair styling, makeup, and a photo shoot that captures not just beauty, but courage.
                </p>
                <p>
                GLO was the dream of Carly, a brave young woman who faced cancer herself. She knew how powerful it could be to look in the mirror and see more than a patient — to see yourself: confident, radiant, and ready to face the future.
                </p>
                
                <p>
                On GLO day, girls leave not only with beautiful photos, but with something even more lasting — renewed strength, the courage to dream big, and the belief that they can move mountains.
                </p>
              </div>
              
              <div className="about-column">
                <div className="program-features">
                  <div className="feature-item">
                    <div className="feature-icon">
                      <Users size={24} />
                    </div>
                    <div className="feature-text">
                      <h4>High School Support</h4>
                      <p>Local high school girls support GLO Girls during makeovers and photoshoots</p>
                    </div>
                  </div>

                  <div className="feature-item">
                    <div className="feature-icon">
                      <Camera size={24} />
                    </div>
                    <div className="feature-text">
                      <h4>Professional Photography</h4>
                      <p>Five photographers capture beautiful moments and memories</p>
                    </div>
                  </div>

                  <div className="feature-item">
                    <div className="feature-icon">
                      <Sparkles size={24} />
                    </div>
                    <div className="feature-text">
                      <h4>Beauty & Wellness</h4>
                      <p>Estheticians and beauticians provide professional makeovers</p>
                    </div>
                  </div>

                  <div className="feature-item">
                    <div className="feature-icon">
                      <Heart size={24} />
                    </div>
                    <div className="feature-text">
                      <h4>Toxin-Free Beauty</h4>
                      <p>Crunchi cosmetics provide safe, toxin-free beauty products</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* New two-row makeup gallery above the scrolling GLO Girls section */}
      <section className="glo-makeup-gallery">
        <div className="fullwidth">
          <div className="makeup-row">
            {row1.map((src) => (
              <button
                key={src}
                className="makeup-tile"
                onClick={() => setActiveImage(src)}
                aria-label="Open image"
              >
                <img src={src} alt="GLO makeover in progress" loading="lazy" />
              </button>
            ))}
          </div>
          <div className="makeup-row">
            {row2.map((src) => (
              <button
                key={src}
                className="makeup-tile"
                onClick={() => setActiveImage(src)}
                aria-label="Open image"
              >
                <img src={src} alt="GLO makeover in progress" loading="lazy" />
              </button>
            ))}
          </div>
        </div>

        {activeImage && (
          <div className="bts-lightbox" role="dialog" aria-modal="true" onClick={() => setActiveImage(null)}>
            <div className="bts-lightbox-inner" onClick={(e) => e.stopPropagation()}>
              <button className="bts-close" aria-label="Close" onClick={() => setActiveImage(null)}>
                ×
              </button>
              <img src={activeImage} alt="GLO makeover enlarged view" />
            </div>
          </div>
        )}
      </section>

      {/* GLO Girls Gallery */}
      <section className="glo-gallery">
        <div className="container">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.4 }}
            className="glo-gallery-content"
          >
            <h2>GLO Girls</h2>
            <p>Meet the incredible young women whose GLO Day transformed more than their look — it reignited their confidence, joy, and dreams.</p>
          </motion.div>
        </div>
        
        <div className="gallery-container">
          <div className="gallery-track">
            <div className="gallery-item">
              <img src="/images/glo/rikki.png" alt="Rikki" />
              <div className="gallery-overlay">
                <h3>Rikki</h3>
              </div>
            </div>

            <div className="gallery-item">
              <img src="/images/glo/morgan.png" alt="Morgan" />
              <div className="gallery-overlay">
                <h3>Morgan</h3>
              </div>
            </div>

            <div className="gallery-item">
              <img src="/images/glo/lily.png" alt="Lily" />
              <div className="gallery-overlay">
                <h3>Lily</h3>
              </div>
            </div>

            <div className="gallery-item">
              <img src="/images/glo/brooklyn.png" alt="Brooklyn" />
              <div className="gallery-overlay">
                <h3>Brooklyn</h3>
              </div>
            </div>

            <div className="gallery-item">
              <img src="/images/glo/makayla.png" alt="Makayla" />
              <div className="gallery-overlay">
                <h3>Makayla</h3>
              </div>
            </div>

            <div className="gallery-item">
              <img src="/images/glo/caitlyn.png" alt="Caitlyn" />
              <div className="gallery-overlay">
                <h3>Caitlyn</h3>
              </div>
            </div>

            <div className="gallery-item">
              <img src="/images/glo/audrey.png" alt="Audrey" />
              <div className="gallery-overlay">
                <h3>Audrey</h3>
              </div>
            </div>

            {/* Duplicate for seamless loop */}
            <div className="gallery-item">
              <img src="/images/glo/rikki.png" alt="Rikki" />
              <div className="gallery-overlay">
                <h3>Rikki</h3>
              </div>
            </div>

            <div className="gallery-item">
              <img src="/images/glo/morgan.png" alt="Morgan" />
              <div className="gallery-overlay">
                <h3>Morgan</h3>
              </div>
            </div>

            <div className="gallery-item">
              <img src="/images/glo/lily.png" alt="Lily" />
              <div className="gallery-overlay">
                <h3>Lily</h3>
              </div>
            </div>

            <div className="gallery-item">
              <img src="/images/glo/brooklyn.png" alt="Brooklyn" />
              <div className="gallery-overlay">
                <h3>Brooklyn</h3>
              </div>
            </div>

            <div className="gallery-item">
              <img src="/images/glo/makayla.png" alt="Makayla" />
              <div className="gallery-overlay">
                <h3>Makayla</h3>
              </div>
            </div>

            <div className="gallery-item">
              <img src="/images/glo/caitlyn.png" alt="Caitlyn" />
              <div className="gallery-overlay">
                <h3>Caitlyn</h3>
              </div>
            </div>

            <div className="gallery-item">
              <img src="/images/glo/audrey.png" alt="Audrey" />
              <div className="gallery-overlay">
                <h3>Audrey</h3>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Connection Section */}
      <section className="glo-connection">
        <div className="container">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 2.4 }}
            className="glo-connection-content"
          >
            <h2>The Carly Faye Foundation Legacy</h2>
            <p>
            GLO is a program offered by the Carly Faye Foundation, carrying forward the legacy of Carly Faye Delong.   
            GLO provides a transformative makeover program for girls who have been diagnosed with pediatric cancer. 
            More than just a makeover, GLO is about restoring hope, rebuilding self-esteem, and reminding each girl 
            that she is strong, beautiful, and capable of chasing her dreams. In keeping with the mission of the 
            Carly Faye Foundation, GLO empowers these young women to look beyond their diagnosis and set bold goals for the future.
            </p>
            <div className="connection-actions">
              <Button 
                href="https://girlsliftingothers.com/" 
                variant="primary" 
                size="large"
                target="_blank"
                rel="noopener noreferrer"
              >
                Visit GLO Website
                <ExternalLink size={16} />
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default GLOPage; 